import { ApiResponse } from 'apisauce';
import api from '../utils/api/api.service';
import errorHelper from '../utils/api/error-helper.service';
import PropertyMapper from '../../mappers/property.mapper';
import { PropertyModel } from '../../models/property.model';
import { PropertyUiModel } from '../../ui-models/property.ui-model';
import { ErrorInfo } from '../../interfaces/error-info.interface';
import { ResponseModel } from '../../interfaces/response-model.interface';

/**
 * function to upload campaign images under user directory
 * @param file image file to upload
 * @param userId user api to store image under
 * @param containerName container name to search for error messages
 */
export function uploadPropertyAsset(file: File, propertyId: string, containerName: string) {
  const data = new FormData();
  data.append('asset', file);
  return api.uploadPropertyAsset(data, propertyId).then((response) => {
    if (response.ok) {
      const { data } = response;
      if (data && data.data) {
        return (data.data as any).Location;
      } else {
        return errorHelper(0, '', containerName, (api.uploadPropertyAsset as any).displayName);
      }
    } else {
      return errorHelper(
        Number(response.status),
        response.data && response.data.errorCode
          ? String(response.data.errorCode || '')
          : response.problem,
        containerName,
        (api.uploadPropertyAsset as any).displayName
      );
    }
  });
}

/**
 * function to fetch every project (property) the current user can see
 * @param containerName container name to search for error messages
 */
export function getAllProperties(containerName: string): Promise<PropertyUiModel[] | ErrorInfo> {
  return api
    .getProperties()
    .then((response: ApiResponse<ResponseModel<PropertyModel[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return PropertyMapper.mapToUIList(data.data);
        } else {
          return errorHelper(0, '', containerName, (api.getProperties as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (api.getProperties as any).displayName
        );
      }
    });
}

/**
 * function to fetch a single project (property) by id
 * @param propertyId project to load
 * @param containerName container name to search for error messages
 */
export function getPropertyDetails(
  propertyId: string,
  containerName: string
): Promise<PropertyUiModel | ErrorInfo> {
  return api
    .getProperty(propertyId)
    .then((response: ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return PropertyMapper.mapToUI(data.data);
        } else {
          return errorHelper(0, '', containerName, (api.getProperty as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (api.getProperty as any).displayName
        );
      }
    });
}

/**
 * function to assign a property manager to a project
 * @param id user id of the manager to assign
 * @param propertyId project to assign the manager to
 * @param containerName container name to search for error messages
 */
export function addPropertyManager(
  id: string,
  propertyId: string,
  containerName: string
): Promise<PropertyUiModel | ErrorInfo> {
  return api
    .addPropertyManager(id, propertyId)
    .then((response: ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return PropertyMapper.mapToUI(data.data);
        } else {
          return errorHelper(0, '', containerName, (api.addPropertyManager as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (api.addPropertyManager as any).displayName
        );
      }
    });
}

/**
 * function to remove a property manager from a project
 * @param id user id of the manager to remove
 * @param propertyId project to remove the manager from
 * @param containerName container name to search for error messages
 */
export function deletePropertyManager(
  id: string,
  propertyId: string,
  containerName: string
): Promise<PropertyUiModel | ErrorInfo> {
  return api
    .deletePropertyManager(id, propertyId)
    .then((response: ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return PropertyMapper.mapToUI(data.data);
        } else {
          return errorHelper(0, '', containerName, (api.deletePropertyManager as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (api.deletePropertyManager as any).displayName
        );
      }
    });
}
